import { useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

import useUnreadCount from "@/hooks/api/notifications/useUnreadCount";
import useFetchNotifications from "@/hooks/api/notifications/useFetchNotifications";
import useMarkAllRead from "@/hooks/api/notifications/useMarkAllRead";

import type { NotificationAudience } from "@/types/notification";

import { NotificationItem } from "./notification-item";
import { useNotificationClick } from "./use-notification-click";
import {
  UNDERLINE_TABS_LIST_CLASS,
  UNDERLINE_TABS_TRIGGER_CLASS,
} from "./notification-utils";

interface NotificationBellProps {
  /** Where "View all" links to (role-specific). */
  viewAllPath?: string;
  /** Scope the bell to one role area's notifications. */
  audience?: NotificationAudience;
}

type ReadFilter = "all" | "unread";
const PREVIEW_SIZE = 8;

/** The role-area notification pages "View all" can point at. */
const VIEW_ALL_PATHS = [
  "/user/notifications",
  "/supervisor/notifications",
  "/admin/notifications",
] as const;

export function NotificationBell({
  viewAllPath = "/user/notifications",
  audience,
}: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<ReadFilter>("all");
  const unreadQuery = useUnreadCount(audience);
  const unread = unreadQuery.data?.unread_count ?? 0;

  // Only fetch the preview list while the popover is open.
  const listQuery = useFetchNotifications(
    {
      page_size: PREVIEW_SIZE,
      is_read: tab === "unread" ? false : undefined,
      audience,
    },
    { enabled: open }
  );
  const notifications = listQuery.data?.results ?? [];

  const markAllRead = useMarkAllRead(audience);
  const handleClick = useNotificationClick(() => setOpen(false));

  // Resolve the free-form prop to one of the known notification routes so the
  // Link stays statically typed; unknown values degrade to the user area.
  const viewAllTo =
    VIEW_ALL_PATHS.find((path) => path === viewAllPath) ??
    "/user/notifications";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative scale-95 rounded-full"
        >
          <Bell className="size-[1.2rem]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium leading-4 text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 mt-2.5 sm:w-96">
        <div className="flex items-center justify-between border-b px-4 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1 has-[>svg]:px-0 text-xs"
            disabled={unread === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            <CheckCheck className="size-3.5" />
            Mark all read
          </Button>
        </div>

        <div className="px-3 pt-1">
          <div className="w-fit border-b">
            <Tabs
              value={tab}
              onValueChange={(v) => setTab(v === "unread" ? "unread" : "all")}
            >
              <TabsList className={UNDERLINE_TABS_LIST_CLASS}>
                <TabsTrigger
                  value="all"
                  className={UNDERLINE_TABS_TRIGGER_CLASS}
                >
                  All
                </TabsTrigger>
                <TabsTrigger
                  value="unread"
                  className={UNDERLINE_TABS_TRIGGER_CLASS}
                >
                  Unread
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <ScrollArea className="h-90">
          {listQuery.isPending ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center text-sm text-muted-foreground">
              <Bell className="size-8 opacity-40" />
              {tab === "unread"
                ? "You're all caught up."
                : "No notifications yet."}
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onClick={handleClick}
                compact
              />
            ))
          )}
        </ScrollArea>

        <div className={cn("border-t")}>
          <Button
            asChild
            variant="ghost"
            size="default"
            className="w-full text-xs rounded-t-none text-primary"
          >
            <Link to={viewAllTo} onClick={() => setOpen(false)}>
              View All Notifications
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
